
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Heart, Search, SlidersHorizontal, Tag, X } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";

export interface FavoriteItem {
  id: number | string;
  title: string;
  description: string;
  brand?: string | null;
  categoryName: string;
  categorySlug: string;
  tag?: string | null;
  keywords?: string | null;
  discountCode?: string | null;
  featured?: boolean | null;
  status?: "draft" | "published";
  sortOrder?: number | null;
  imageUrl?: string | null;
  href: string;
  openInNewTab: boolean;
  ctaLabel: string;
}

export type FavoritesAdminControls = {
  enabled: boolean;
  toolbar?: ReactNode;
  loading?: boolean;
  message?: string;
  error?: string;
  dragIndex?: number | null;
  dropIndex?: number | null;
  onDragStart?: (sourceIndex: number) => void;
  onDragOver?: (targetIndex: number) => void;
  onDrop?: (targetIndex: number) => void;
  onDragEnd?: () => void;
  renderCardMeta?: (item: FavoriteItem, sourceIndex: number) => ReactNode;
};

type FavoritesPageProps = {
  items: FavoriteItem[];
  adminControls?: FavoritesAdminControls;
  showHero?: boolean;
  showSiteChrome?: boolean;
  /** Public-page loading flag — renders skeleton cards so height stays stable. */
  loading?: boolean;
};

function normalizeImageUrl(rawUrl?: string | null): string | null {
  if (!rawUrl) return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  // Handle legacy values that accidentally include srcset width descriptors.
  const firstToken = trimmed.split(/\s+/)[0];
  try {
    return new URL(firstToken).toString();
  } catch {
    return firstToken;
  }
}

function FavoriteCardImage({
  item,
  accentColor,
}: {
  item: FavoriteItem;
  accentColor: string;
}) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imageUrl = normalizeImageUrl(item.imageUrl);

  if (!imageUrl || failed) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ backgroundColor: `${accentColor}14` }}
      >
        <Tag size={24} style={{ color: accentColor }} />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={item.title}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ opacity: loaded ? 1 : 0, transition: "opacity 300ms ease" }}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
    />
  );
}

export default function FavoritesPage({
  items,
  adminControls,
  showHero = true,
  showSiteChrome = true,
  loading = false,
}: FavoritesPageProps) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const dragImageRef = useRef<HTMLElement | null>(null);
  const theme = usePalette();
  const adminEnabled = adminControls?.enabled === true;

  const orderedItems = useMemo(() => {
    if (adminEnabled) return items;

    return [...items].sort((a, b) => {
      const sortA = a.sortOrder ?? 0;
      const sortB = b.sortOrder ?? 0;
      if (sortA !== sortB) return sortA - sortB;

      return a.title.localeCompare(b.title);
    });
  }, [adminEnabled, items]);

  const categories = useMemo(() => {
    const categoryMap = new Map<string, string>();
    orderedItems.forEach((item) => {
      categoryMap.set(item.categorySlug, item.categoryName);
    });

    return [
      { slug: "all", label: "All Favorites" },
      ...Array.from(categoryMap.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([slug, label]) => ({ slug, label })),
    ];
  }, [orderedItems]);

  const filtered = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return orderedItems
      .map((item, sourceIndex) => ({ item, sourceIndex }))
      .filter(({ item }) => {
        const matchesCategory =
          activeCategory === "all" || item.categorySlug === activeCategory;
        if (!matchesCategory) return false;

        if (!normalizedSearch) return true;

        const haystack = [
          item.title,
          item.brand ?? "",
          item.description,
          item.keywords ?? "",
          item.tag ?? "",
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      });
  }, [orderedItems, activeCategory, search]);

  return (
    <div className={showHero || showSiteChrome ? "min-h-dvh" : ""} style={{ backgroundColor: colors.cream }}>

      {showHero ? (
        <section className="pt-28 pb-12 px-6 text-center relative overflow-hidden">
          <Blob className="w-80 h-80 top-10 left-1/4" color={theme.blob1} />
          <Blob className="w-64 h-64 bottom-0 right-1/4" color={theme.blob2} />

          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="relative z-10 max-w-3xl mx-auto"
          >
            <motion.div
              variants={fadeUp}
              custom={0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs tracking-widest uppercase mb-6"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                backgroundColor: `${theme.accent}12`,
                color: theme.accent,
              }}
            >
              <Heart size={14} />
              Chelsea-Approved Picks
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-5xl sm:text-6xl lg:text-7xl leading-tight mb-4"
              style={{
                fontFamily: fonts.logo,
                fontWeight: 400,
                color: colors.navy,
              }}
            >
              My{" "}
              <span
                style={{
                  fontFamily: fonts.heading,
                  color: theme.warm,
                  fontSize: "1.15em",
                }}
              >
                Favorite
              </span>{" "}
              Things
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="max-w-lg mx-auto leading-relaxed mb-2"
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: `${colors.navy}99`,
                letterSpacing: "0.03em",
              }}
            >
              Curated links to products, tools, stores, and picks I genuinely use
              and love.
            </motion.p>

            <motion.p
              variants={fadeUp}
              custom={3}
              className="text-xs"
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: `${colors.navy}44`,
              }}
            >
              Some links are affiliate links and may earn me a commission at no
              extra cost to you.
            </motion.p>
          </motion.div>
        </section>
      ) : null}

      <section
        className={`px-6 pb-6 sticky ${
          showSiteChrome ? "top-[73px]" : "top-0"
        } z-30 backdrop-blur-md`}
        style={{ backgroundColor: `${colors.cream}f2` }}
      >
        <div className="max-w-6xl mx-auto">
          {adminControls?.toolbar ? (
            <div
              className="mb-4 p-4 rounded-2xl border"
              style={{
                borderColor: `${colors.tan}22`,
                backgroundColor: "white",
              }}
            >
              {adminControls.toolbar}
            </div>
          ) : null}

          <div
            className="flex flex-col lg:flex-row lg:items-center gap-3 py-4 border-b"
            style={{ borderColor: `${colors.tan}12` }}
          >
            <div className="flex items-center gap-2 lg:flex-1 lg:max-w-xl">
              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: `${colors.navy}44` }}
                />
                <label className="sr-only" htmlFor="favorite-search">
                  Search favorites
                </label>
                <input
                  id="favorite-search"
                  type="text"
                  placeholder="Search favorites..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-full border-2 border-transparent text-sm outline-none transition-all duration-200"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 500,
                    color: colors.navy,
                    backgroundColor: "white",
                    letterSpacing: "0.03em",
                  }}
                />
              </div>

              {/* Mobile filter trigger — opens category sheet */}
              <motion.button
                type="button"
                onClick={() => setFilterOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={filterOpen}
                whileTap={{ scale: 0.94 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className="lg:hidden relative inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs tracking-wide cursor-pointer shrink-0 shadow-sm"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  backgroundColor: activeCategory === "all" ? "white" : theme.accent,
                  color: activeCategory === "all" ? `${colors.navy}88` : "white",
                }}
              >
                <SlidersHorizontal size={14} />
                Filter
                {activeCategory !== "all" && (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: "white" }}
                  />
                )}
              </motion.button>
            </div>

            {/* Desktop inline pills */}
            <div className="hidden lg:flex flex-wrap items-center gap-2">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setActiveCategory(category.slug)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs tracking-wide transition-all duration-200 cursor-pointer ${
                    activeCategory === category.slug
                      ? "text-white shadow-md"
                      : "bg-white hover:bg-opacity-80"
                  }`}
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    ...(activeCategory === category.slug
                      ? { backgroundColor: theme.accent }
                      : { color: `${colors.navy}88` }),
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-3 flex items-center justify-between">
            <p
              className="text-xs"
              style={{
                fontFamily: fonts.body,
                fontWeight: 600,
                color: `${colors.navy}55`,
              }}
            >
              Showing {filtered.length} of {orderedItems.length} favorites
            </p>
            {(activeCategory !== "all" || search) && (
              <button
                onClick={() => {
                  setActiveCategory("all");
                  setSearch("");
                }}
                className="text-xs underline cursor-pointer"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 600,
                  color: theme.accent,
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Mobile filter bottom-sheet */}
      <AnimatePresence>
        {filterOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end"
          role="dialog"
          aria-modal="true"
          aria-label="Filter favorites by category"
        >
          <motion.button
            type="button"
            aria-label="Close filters"
            onClick={() => setFilterOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            className="relative rounded-t-3xl p-6 pb-8 max-h-[75dvh] overflow-y-auto shadow-2xl"
            style={{ backgroundColor: colors.cream }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3
                className="text-sm tracking-[0.15em] uppercase"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  color: colors.navy,
                }}
              >
                Filter by Category
              </h3>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="Close filters"
                className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer"
                style={{ backgroundColor: "white", color: colors.navy }}
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => {
                    setActiveCategory(category.slug);
                    setFilterOpen(false);
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs tracking-wide transition-all duration-200 cursor-pointer ${
                    activeCategory === category.slug
                      ? "text-white shadow-md"
                      : "bg-white"
                  }`}
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    ...(activeCategory === category.slug
                      ? { backgroundColor: theme.accent }
                      : { color: `${colors.navy}88` }),
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
        )}
      </AnimatePresence>

      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {adminEnabled && adminControls?.message ? (
            <p
              className="mb-4 text-sm"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: "#606b4c",
              }}
            >
              {adminControls.message}
            </p>
          ) : null}

          {adminEnabled && adminControls?.error ? (
            <p
              className="mb-4 text-sm"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: "#b04b64",
              }}
            >
              {adminControls.error}
            </p>
          ) : null}

          {adminEnabled && adminControls?.loading ? (
            <p
              className="text-sm mb-4"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: `${colors.navy}88`,
              }}
            >
              Loading favorites...
            </p>
          ) : null}

          {!adminControls?.loading && filtered.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(({ item, sourceIndex }) => (
                <article
                  key={item.id}
                  draggable={adminEnabled}
                  onDragStart={(event) => {
                    if (!adminEnabled) return;

                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", String(item.id));

                    const card = event.currentTarget;
                    const clone = card.cloneNode(true) as HTMLElement;
                    clone.style.position = "fixed";
                    clone.style.top = "-9999px";
                    clone.style.left = "-9999px";
                    clone.style.pointerEvents = "none";
                    clone.style.width = `${card.offsetWidth}px`;
                    clone.style.opacity = "0.97";
                    clone.style.transform = "rotate(1.2deg)";
                    clone.style.boxShadow = "0 18px 36px rgba(8,45,61,0.25)";
                    document.body.appendChild(clone);
                    dragImageRef.current = clone;

                    const rect = card.getBoundingClientRect();
                    const offsetX = Math.max(0, event.clientX - rect.left);
                    const offsetY = Math.max(0, event.clientY - rect.top);
                    event.dataTransfer.setDragImage(clone, offsetX, offsetY);

                    adminControls?.onDragStart?.(sourceIndex);
                  }}
                  onDragOver={(event) => {
                    if (!adminEnabled) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    adminControls?.onDragOver?.(sourceIndex);
                  }}
                  onDrop={(event) => {
                    if (!adminEnabled) return;
                    event.preventDefault();
                    adminControls?.onDrop?.(sourceIndex);
                  }}
                  onDragEnd={() => {
                    if (dragImageRef.current?.parentNode) {
                      dragImageRef.current.parentNode.removeChild(
                        dragImageRef.current
                      );
                    }
                    dragImageRef.current = null;
                    adminControls?.onDragEnd?.();
                  }}
                  className={`group rounded-2xl overflow-hidden transition-all duration-300 ${
                    adminEnabled ? "" : "hover:shadow-xl hover:-translate-y-1"
                  }`}
                  style={{
                    backgroundColor: colors.cream,
                    cursor: adminEnabled ? "grab" : "default",
                    opacity:
                      adminEnabled && adminControls?.dragIndex === sourceIndex
                        ? 0.68
                        : 1,
                    boxShadow:
                      adminEnabled && adminControls?.dragIndex === sourceIndex
                        ? "0 16px 30px rgba(8,45,61,0.24)"
                        : adminEnabled &&
                            adminControls?.dropIndex === sourceIndex &&
                            adminControls?.dragIndex !== sourceIndex
                          ? `0 0 0 3px ${theme.accent}55, 0 14px 28px rgba(8,45,61,0.14)`
                        : undefined,
                    transition:
                      "transform 180ms ease, box-shadow 180ms ease, opacity 140ms ease",
                    transform:
                      adminEnabled &&
                      adminControls?.dropIndex === sourceIndex &&
                      adminControls?.dragIndex !== sourceIndex
                        ? "translateY(-2px)"
                        : undefined,
                  }}
                >
                  <div
                    className="h-44 relative overflow-hidden border-b"
                    style={{ borderColor: `${colors.tan}15`, backgroundColor: `${theme.accent}14` }}
                  >
                    <FavoriteCardImage item={item} accentColor={theme.accent} />

                    {item.tag && (
                      <span
                        className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] tracking-widest uppercase text-white"
                        style={{
                          fontFamily: fonts.body,
                          fontWeight: 700,
                          backgroundColor: theme.accent,
                        }}
                      >
                        {item.tag}
                      </span>
                    )}

                    {adminEnabled ? (
                      <span
                        className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase"
                        style={{
                          fontFamily: fonts.body,
                          fontWeight: 700,
                          color: colors.navy,
                          backgroundColor: "white",
                        }}
                      >
                        Drag
                      </span>
                    ) : null}
                  </div>

                  <div className="p-5">
                    <p
                      className="text-[10px] tracking-[0.15em] uppercase mb-1"
                      style={{
                        fontFamily: fonts.body,
                        fontWeight: 700,
                        color: theme.accent,
                      }}
                    >
                      {item.brand || item.categoryName}
                    </p>

                    <h3
                      className="text-base mb-2 leading-snug"
                      style={{
                        fontFamily: fonts.body,
                        fontWeight: 700,
                        color: colors.navy,
                      }}
                    >
                      {item.title}
                    </h3>

                    <p
                      className="text-sm leading-relaxed mb-4 line-clamp-3"
                      style={{
                        fontFamily: fonts.body,
                        fontWeight: 500,
                        color: `${colors.navy}99`,
                        letterSpacing: "0.03em",
                      }}
                    >
                      {item.description}
                    </p>

                    {item.discountCode && (
                      <p
                        className="text-xs mb-4"
                        style={{
                          fontFamily: fonts.body,
                          fontWeight: 700,
                          color: theme.accent,
                        }}
                      >
                        Code: {item.discountCode}
                      </p>
                    )}

                    {adminEnabled && adminControls?.renderCardMeta ? (
                      <div className="mb-4">
                        {adminControls.renderCardMeta(item, sourceIndex)}
                      </div>
                    ) : null}

                    {item.openInNewTab ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-xs tracking-wide w-full justify-center cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          fontFamily: fonts.button,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                          backgroundColor: theme.accent,
                        }}
                      >
                        {item.ctaLabel}
                        <ExternalLink size={12} />
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-xs tracking-wide w-full justify-center cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          fontFamily: fonts.button,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                          backgroundColor: theme.accent,
                        }}
                      >
                        {item.ctaLabel}
                      </Link>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden border"
                  style={{ backgroundColor: colors.cream, borderColor: `${colors.tan}25` }}
                >
                  <div className="h-44 animate-pulse" style={{ backgroundColor: `${theme.accent}14` }} />
                  <div className="p-5">
                    <div className="h-2.5 rounded animate-pulse mb-3" style={{ backgroundColor: `${colors.navy}10`, width: "40%" }} />
                    <div className="h-4 rounded animate-pulse mb-2" style={{ backgroundColor: `${colors.navy}10`, width: "85%" }} />
                    <div className="h-3 rounded animate-pulse mb-1.5" style={{ backgroundColor: `${colors.navy}0d` }} />
                    <div className="h-3 rounded animate-pulse mb-4" style={{ backgroundColor: `${colors.navy}0d`, width: "70%" }} />
                    <div className="h-9 rounded-full animate-pulse" style={{ backgroundColor: `${theme.accent}1f`, width: "50%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <p
                className="text-lg mb-2"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  color: colors.navy,
                }}
              >
                No favorites found
              </p>
              <p
                className="text-sm mb-4"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  color: `${colors.navy}77`,
                }}
              >
                Try a different search or category
              </p>
            </div>
          )}
        </div>
      </section>

      {showSiteChrome ? <Footer /> : null}
    </div>
  );
}
